import {BindingsController, isInInterval} from '@mintdata-wsp/common/controllers/bindings/BindingsController';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {BindingViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {BindingPhase} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/BindingPhase';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {NamedRange, RangeSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {R1C1Location, R1C1Range} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {
    BindingDirection,
    IBinding,
    RepeaterFixation
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {EnumUtils} from '@mintdata-wsp/common/util/EnumUtil';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {BindingFormatBehavior} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import bind from 'bind-decorator';
import {action, runInAction} from 'mobx';
import * as React from 'react';

type RangeInfo = {
    range:NamedRange;
    editSheetId:SheetId;
    startLocation:MeshLocation;
    endLocation:MeshLocation
}

export class WspBindingsController extends BindingsController
{
    @action.bound
    public async handleMeshMouseDown(ctx:IWSPMeshContext, cellAtCursor:MeshLocation, event:React.MouseEvent<HTMLElement>)
    {
        const viewState = this.getViewState();
        const bindingPhase = viewState.bindingPhase;

        if( bindingPhase === BindingPhase.New )
        {
            viewState.bindingPhase = BindingPhase.NewExtend;
            viewState.selectionStartLocation = cellAtCursor.withDefaults();
            this._checkActiveCell(ctx, cellAtCursor);
        }
        else
            this.stopBinding();
    }

    @action.bound
    public async handleMeshMouseMove(ctx:IWSPMeshContext, cellAtCursor:MeshLocation, event:React.MouseEvent<HTMLElement>)
    {
        const viewState = this.getViewState();
        const bindingPhase = viewState.bindingPhase;

        if( bindingPhase === BindingPhase.New )
        {
            viewState.selectionStartLocation = cellAtCursor.withDefaults();
            this._checkActiveCell(ctx, cellAtCursor);
        }
        else if( bindingPhase === BindingPhase.NewExtend || bindingPhase === BindingPhase.EditExtend )
        {
            viewState.selectionEndLocation = cellAtCursor.withDefaults();
            this._checkActiveCell(ctx, cellAtCursor);
        }
    }

    @action.bound
    public async handleMeshMouseUp(ctx:IWSPMeshContext, cellAtCursor:MeshLocation, event:React.MouseEvent<HTMLElement>)
    {
        const viewState = this.getViewState();
        const bindingPhase = viewState.bindingPhase;

        if( bindingPhase === BindingPhase.NewExtend || bindingPhase === BindingPhase.EditExtend )
            viewState.selectionEndLocation = cellAtCursor.withDefaults();

        this._commitCurrentBindingOperation(ctx, event.shiftKey, event.altKey);
    }

    @action.bound
    public async handleMeshMouseScroll(ctx:IWSPMeshContext, scrollTo:MeshLocation)
    {
        // TODO[ranges]: fix this behaviour
        // just do whatever we do on mouse move
        // this.handleMeshMouseMove(ctx, scrollTo, null);
    }

    public async handleDocumentMouseUp(ctx:IWSPMeshContext, event:MouseEvent)
    {
        this._commitCurrentBindingOperation(ctx, event.shiftKey, event.altKey);
    }

    private _commitCurrentBindingOperation(ctx:IWSPMeshContext, isFixedDown:boolean, isFixedRight:boolean)
    {
        const viewState = this.getViewState();
        const bindingPhase = viewState.bindingPhase;

        if( bindingPhase == BindingPhase.NewExtend )
            this._commitBindingCreation(ctx, EnumUtils.createFixation(isFixedDown, isFixedRight));
        else if( bindingPhase == BindingPhase.EditExtend )
            this._commitPropBindingExtension(ctx);
    }

    @bind
    public getViewState():BindingViewState
    {
        let openDesign = commonControllers.designState.getOpenDesign();
        if( !openDesign )
            return;

        return openDesign.designState.binding.viewState;
    }

    @bind
    public isBindingInProgress():boolean
    {
        const viewState = this.getViewState();
        return viewState && !!viewState.bindingPhase;
    }

    @action.bound
    public async startEventBinding(eventProp:EventProp)
    {
        const {mesh} = controllers.ui.spreadsheets;

        this._clearCurrentBindingViewState();
        const viewState = this.getViewState();

        Assert.no(eventProp.bindingId, 'Existing binding should be edited instead');

        viewState.bindingPhase = BindingPhase.New;
        viewState.currentEventProp = eventProp;
        viewState.selectionStartLocation = new MeshLocation(0, 0);

        mesh.focusActiveSheet();
    }

    private _checkActiveCell(ctx:IWSPMeshContext, cellAtCursor:MeshLocation)
    {
        const {meshSelection} = controllers.ui.spreadsheets;

        cellAtCursor = cellAtCursor.withDefaults();

        meshSelection.checkActiveCellCol(ctx, cellAtCursor.colIndex);
        meshSelection.checkActiveCellRow(ctx, cellAtCursor.rowIndex);
    }

    @action.bound
    public async startPropBinding(prop:Prop<any>, propPath:PropPath):Promise<void>
    {
        const {mesh} = controllers.ui.spreadsheets;

        this._clearCurrentBindingViewState();
        const viewState = this.getViewState();

        const existingBinding = prop.getBinding(propPath);
        Assert.no(existingBinding, 'Existing binding should be edited instead');

        viewState.currentProp = prop;
        viewState.currentPath = propPath;
        viewState.bindingPhase = BindingPhase.New;
        viewState.selectionStartLocation = new MeshLocation(0, 0);

        mesh.focusActiveSheet();
    }

    @action.bound
    public async editPropBinding(prop:Prop<any>, propPath:PropPath):Promise<void>
    {
        this._clearCurrentBindingViewState();
        const viewState = this.getViewState();

        const editBinding = prop.getBinding(propPath);
        Assert.yes(editBinding, 'Binding not found');

        const rangeInfo = await this._prepareNamedRangeEditing(editBinding.range);
        if( !rangeInfo )
            return;

        const {range, editSheetId, startLocation, endLocation} = rangeInfo;

        runInAction(() =>
        {
            viewState.bindingPhase = BindingPhase.Edit;
            viewState.currentProp = prop;
            viewState.currentPath = propPath;
            viewState.editPropBinding = editBinding;

            viewState.editNamedRange = range;
            viewState.editSheetId = editSheetId;
            viewState.selectionStartLocation = startLocation;
            viewState.selectionEndLocation = endLocation;
        });

        this._scrollToCell(editSheetId, startLocation);
    }

    @action.bound
    public async editEventBinding(eventProp:EventProp):Promise<void>
    {
        this._clearCurrentBindingViewState();
        const viewState = this.getViewState();

        const editBinding = this.getEventBinding(eventProp.bindingId);
        Assert.yes(editBinding, 'Binding not found');

        const rangeInfo = await this._prepareNamedRangeEditing(editBinding.range);
        if( !rangeInfo )
            return;

        const {range, editSheetId, startLocation, endLocation} = rangeInfo;

        runInAction(() =>
        {
            viewState.bindingPhase = BindingPhase.Edit;
            viewState.currentEventProp = eventProp;
            viewState.editEventBinding = editBinding;

            viewState.editNamedRange = range;
            viewState.editSheetId = editSheetId;
            viewState.selectionStartLocation = startLocation;
            viewState.selectionEndLocation = endLocation;
        });

        this._scrollToCell(editSheetId, startLocation);
    }

    @action.bound
    private async _prepareNamedRangeEditing(range:NamedRange):Promise<RangeInfo | null>
    {
        const {main, mesh, namedRanges, frameDetails} = controllers.ui.spreadsheets;

        // error here can happen, if nested frame has invalid structure
        const rangeSizeOrError = namedRanges.getNamedRangeSize(range.id);
        if( Type.isError(rangeSizeOrError) )
            return null;

        const mainSheet = main.findAnySheetById(range.sheetId);

        mesh.focusSheetById(range.sheetId);

        const rangeSize = rangeSizeOrError as RangeSize;

        let editSheetId;
        let startLocation;
        if( !range.framePath )
        {
            editSheetId = mainSheet.id;
            startLocation = MeshLocation.fromSlotLocation(range.location);

            await frameDetails.exitNestedView(mainSheet.workbook.id, mainSheet.id, mainSheet.id);
        }
        else
        {
            const nestedSheetInfo =
                await frameDetails.enterNestedViewByPath(mainSheet.workbook.id, mainSheet.id, range.location, range.framePath);
            if( !nestedSheetInfo ) return;

            const {sheet, rowIndex, colIndex} = nestedSheetInfo;
            editSheetId = sheet.id;
            startLocation = new MeshLocation(colIndex, rowIndex);
        }

        const endLocation = startLocation
            .translate(rangeSize.rowCount - 1, rangeSize.colCount - 1);

        return {
            range,
            editSheetId,
            startLocation,
            endLocation
        };
    }

    private _scrollToCell(editSheetId:SheetId, location:MeshLocation):void
    {
        const {tabs, main, mesh, meshSelection, workbook} = controllers.ui.spreadsheets;

        tabs.setActiveSheet(main.getRootWorkbook().id, editSheetId);
        mesh.focusSheetById(editSheetId);

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(editSheetId);

        mesh.onFocusMesh(ctx);

        meshSelection.selectCell(ctx, location);
        meshSelection.checkActiveCellCol(ctx, location.colIndex);
        meshSelection.checkActiveCellRow(ctx, location.rowIndex);
    }

    @action.bound
    private async _commitBindingCreation(ctx:IWSPMeshContext,
                                         repeaterFixation:RepeaterFixation)
    {
        const viewState = this.getViewState();
        Assert.yes(viewState.bindingPhase === BindingPhase.NewExtend);

        if( viewState.currentProp )
            this._createPropBinding(ctx, repeaterFixation);
        else
            this._createEventBinding(ctx, repeaterFixation);
    }

    @action.bound
    private async _createPropBinding(ctx:IWSPMeshContext,
                                     repeaterFixation:RepeaterFixation)
    {
        const {frameDetails} = controllers.ui.spreadsheets;
        const {workbookId, sheetId, mainSheetId} = ctx;

        const viewState = this.getViewState();

        const prop = viewState.currentProp;
        const propPath = viewState.currentPath;
        const useFormat = prop.bindingFormatBehavior(propPath) === BindingFormatBehavior.On
            && Type.String.isEqualTo(prop.getPropType(propPath));

        const selectionBounds = viewState.normalizedSelectionBounds;
        const startLoc = selectionBounds.startLoc.toSlotLocation();
        const endLoc = selectionBounds.endLoc.toSlotLocation();
        const isDown = selectionBounds.isDown;

        const rangeRowCount = endLoc.rowIndex - startLoc.rowIndex + 1;
        const rangeColCount = endLoc.colIndex - startLoc.colIndex + 1;
        const bindingDirection = isDown ? BindingDirection.Down : BindingDirection.Right;

        const {mainLocation, framePath} = frameDetails.computeFramePathByContext(ctx, startLoc);

        if( ctx.sheetId === ctx.mainSheetId )
            this._setPropValueIntoSheet(ctx, prop, propPath, startLoc.rowIndex, startLoc.colIndex, rangeRowCount, rangeColCount, isDown);

        this.createPropBinding(prop, propPath,
            workbookId, mainSheetId, mainLocation, framePath,
            rangeRowCount, rangeColCount,
            repeaterFixation, bindingDirection, useFormat
        );

        // enter edit mode right away
        await this.editPropBinding(prop, propPath);
    }

    @action.bound
    private async _createEventBinding(ctx:IWSPMeshContext,
                                      repeaterFixation:RepeaterFixation)
    {
        const {frameDetails} = controllers.ui.spreadsheets;
        const {workbookId, sheetId, mainSheetId} = ctx;

        // only allow event bindings on top level sheets
        if( sheetId !== mainSheetId )
            return null;

        const viewState = this.getViewState();
        const eventProp = viewState.currentEventProp;
        const endLocation = viewState.selectionEndLocation;

        const {mainLocation, framePath} = frameDetails.computeFramePathByContext(ctx, endLocation.toSlotLocation());
        this.createEventBinding(eventProp, workbookId, mainSheetId, mainLocation, framePath, repeaterFixation);

        // enter edit mode right away
        await this.editEventBinding(eventProp);
    }

    @action.bound
    public async startPropBindingExtension(ctx:IWSPMeshContext):Promise<void>
    {
        const viewState = this.getViewState();
        if( viewState.bindingPhase === BindingPhase.Edit )
        {
            viewState.bindingPhase = BindingPhase.EditExtend;
        }
    }

    @action.bound
    private _commitPropBindingExtension(ctx:IWSPMeshContext):void
    {
        const viewState = this.getViewState();
        const {bindingPhase, editPropBinding, editNamedRange} = viewState;
        Assert.yes(viewState.bindingPhase === BindingPhase.EditExtend);

        const {isDown, startLoc, endLoc} = viewState.normalizedSelectionBounds;
        const rangeRowCount = endLoc.rowIndex - startLoc.rowIndex + 1;
        const rangeColCount = endLoc.colIndex - startLoc.colIndex + 1;

        editNamedRange.rowCount = rangeRowCount;
        editNamedRange.colCount = rangeColCount;

        // move back to edit phase
        viewState.bindingPhase = BindingPhase.Edit;

        // restart binding editing to pick up new state
        this.editPropBinding(viewState.currentProp, viewState.currentPath);
    }

    @action.bound
    public editBindingFixation(ctx:IWSPMeshContext, newFixation:RepeaterFixation):void
    {
        const {bindingPhase, editBinding} = this.getViewState();
        if( bindingPhase !== BindingPhase.Edit && bindingPhase === BindingPhase.EditExtend )
            return;

        editBinding.repeaterFixation = newFixation;
    }

    @action.bound
    public togglePropBindingDirection(ctx:IWSPMeshContext):void
    {
        const {bindingPhase, editPropBinding, editNamedRange, currentProp, currentPath} = this.getViewState();
        if( bindingPhase !== BindingPhase.Edit && bindingPhase === BindingPhase.EditExtend )
            return;

        editPropBinding.direction = editPropBinding.direction === BindingDirection.Down
            ? BindingDirection.Right
            : BindingDirection.Down;

        const {rowCount, colCount} = editNamedRange;
        editNamedRange.rowCount = colCount;
        editNamedRange.colCount = rowCount;

        // restart binding editing to pick up new state
        this.editPropBinding(currentProp, currentPath);
    }

    @action.bound
    public togglePropBindingAutoMode(ctx:IWSPMeshContext):void
    {
        const {bindingPhase, editPropBinding, editNamedRange, currentProp, currentPath} = this.getViewState();
        if( bindingPhase !== BindingPhase.Edit && bindingPhase === BindingPhase.EditExtend )
            return;

        if( editNamedRange.isAuto() )
        {
            const {namedRanges} = controllers.ui.spreadsheets;
            const rangeSizeOrError = namedRanges.getNamedRangeSize(editNamedRange.id);
            if( Type.isError(rangeSizeOrError) )
                return;

            const rangeSize = rangeSizeOrError as RangeSize;

            editNamedRange.rowCount = Math.max(1, rangeSize.rowCount);
            editNamedRange.colCount = Math.max(1, rangeSize.colCount);
        }
        else
        {
            editNamedRange.setAuto(editPropBinding.direction === BindingDirection.Down);
        }

        // restart binding editing to pick up new state
        this.editPropBinding(currentProp, currentPath);
    }

    @action.bound
    public togglePropBindingUseFormat(ctx:IWSPMeshContext):void
    {
        const {bindingPhase, editPropBinding} = this.getViewState();
        if( bindingPhase !== BindingPhase.Edit && bindingPhase === BindingPhase.EditExtend )
            return;

        editPropBinding.useFormat = !editPropBinding.useFormat;
    }

    @action.bound
    public stopBinding():void
    {
        this._clearCurrentBindingViewState();
    }

    protected _clearCurrentBindingViewState():void
    {
        this.getViewState().clear();
    }

    @bind
    public findBindingsForLocation(sheetId:SheetId, colIndex:number, rowIndex:number):Array<IBinding>
    {
        const {namedRanges} = controllers.ui.spreadsheets;

        return Array
            .from(this.getRuntimeState().bindings.values())
            .filter(binding =>
            {
                const range = binding.range;
                if( range.sheetId !== sheetId ) return false;

                const rangeSizeOrError = namedRanges.getNamedRangeSize(range.id);
                if( Type.isError(rangeSizeOrError) ) return false;

                const rangeSize = rangeSizeOrError as RangeSize;
                if( range.framePath )
                    return range.location.rowIndex === rowIndex && range.location.colIndex === colIndex;

                return isInInterval(rowIndex, range.location.rowIndex, rangeSize.rowCount) &&
                    isInInterval(colIndex, range.location.colIndex, rangeSize.colCount);
            });
    }

    @bind
    public getBindingExpression(binding:IBinding, repeater:Repeater):string
    {

        const {range, repeaterFixation} = binding;
        const {location, rowCount, colCount} = binding.range;
        const {rowIndex, colIndex} = location;

        const isFixedDown = EnumUtils.isFixedDown(repeaterFixation);
        const isFixedRight = EnumUtils.isFixedRight(repeaterFixation);

        const from = new R1C1Location(
            isFixedDown ? rowIndex + 1 : 0, isFixedDown,
            isFixedRight ? colIndex + 1 : 0, isFixedRight
        );
        if( rowCount === 1 && colCount === 1 )
            return `@${from.toA1String(colIndex, rowIndex)}`;

        const toRowOffset = range.isAutoVertical() ? null : (isFixedDown ? rowIndex + rowCount : rowCount - 1);
        const toColOffset = range.isAutoHorizontal() ? null : (isFixedRight ? colIndex + colCount : colCount - 1);
        const to = new R1C1Location(
            toRowOffset, isFixedDown,
            toColOffset, isFixedRight
        );

        const fromTo = new R1C1Range(from, to);
        return `@${fromTo.toA1String(colIndex, rowIndex)}`;
    }

    private _setPropValueIntoSheet(ctx:IWSPMeshContext, prop:Prop<any>, propPath:PropPath, rowIndex:number, colIndex:number, rowCount:number, colCount:number, isDown:boolean)
    {
        const {sheetId, sheetCtrl} = ctx;

        const setValueToLocationFn = (rowIndex, colIndex, propValue) =>
        {
            const location = new SlotLocation(colIndex, rowIndex);
            const slot = sheetCtrl.findSlot(sheetId, location);

            if( AnySlot.isSlotEmpty(slot) )
            {
                let value;
                if( Type.isInstanceOfIValue(propValue) )
                    value = propValue as IValue;
                else if( Type.hasToIValue(propValue) )
                    value = (propValue as any).toIValue();
                else if( propValue )
                    value = AString.fromString(propValue.toString());
                else
                    value = AEmpty.Value;

                sheetCtrl.setSlotFormula(sheetId, location, value, true);
            }
        };

        const propValue = prop.getPropValue(propPath);
        const propConfig = prop.getPropertyConfig(propPath);
        if( !propConfig.isArray )
        {
            setValueToLocationFn(rowIndex, colIndex, propValue);
            return;
        }

        if( !Array.isArray(propValue) )
            return;

        const arrayValue = propValue as Array<any>;
        const length = Math.min(arrayValue.length, isDown ? rowCount : colCount);
        const width = Math.min(propConfig.width, isDown ? colCount : rowCount);

        const fieldNames = propConfig.propertyNames;
        for( let i = 0; i < length; i++ )
        {
            const item = arrayValue[i];
            for( let j = 0; j < width; j++ )
            {
                const fieldValue = propConfig.isPrimitive
                    ? item
                    : item[fieldNames[j]];

                setValueToLocationFn(
                    rowIndex + (isDown ? i : j),
                    colIndex + (isDown ? j : i),
                    fieldValue
                );
            }
        }
    }
}
